import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL, buildPublicShareUrl, CURRENT_ORIGIN_IS_PRIVATE, HAS_PUBLIC_SHARE_ORIGIN } from '../config';
import Swal from 'sweetalert2';
import { formatCurrency, formatTotalCurrency } from '../utils/formatUtils';
import { toast } from 'react-hot-toast';
import { copyToClipboard } from '../utils/clipboard';
import ShoppingItemCard from './ShoppingItemCard';
import AlternativeProductModal from './AlternativeProductModal';
import PartnerInviteModal from './PartnerInviteModal';

function buildShoppingListUiState({ list, step }) {
    const isCompleted = list?.status === 'completed';
    const isAwaitingOwnerConfirmation = list?.status === 'awaiting_owner_confirmation';

    if (isCompleted) {
        return {
            cardMode: 'VIEW',
            canEditItems: false,
            canDeleteList: false,
            leftAction: 'completed',
            rightAction: 'close'
        };
    }

    if (step === 'FINALIZING') {
        return {
            cardMode: 'FINAL',
            canEditItems: true,
            canDeleteList: true,
            leftAction: 'back',
            rightAction: 'complete'
        };
    }

    if (isAwaitingOwnerConfirmation) {
        return {
            cardMode: 'FINAL',
            canEditItems: true,
            canDeleteList: false,
            leftAction: 'back',
            rightAction: 'complete'
        };
    }

    return {
        cardMode: 'CHECK',
        canEditItems: true,
        canDeleteList: true,
        leftAction: 'share',
        rightAction: 'finalize'
    };
}

function resolveDisplayListName(list) {
    const rawName = String(list?.name || '').trim();
    if (!rawName || rawName === '0') {
        return 'Alışveriş Onayı';
    }

    return rawName;
}

const ShoppingListDetailModal = ({ list, isOpen, autoStartFinalizing = false, onClose, onComplete, onDeleteList }) => {
    const [items, setItems] = useState([]);
    const [isCompleting, setIsCompleting] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [showPartnerInvite, setShowPartnerInvite] = useState(false);
    const [step, setStep] = useState('SHOPPING'); // 'SHOPPING' or 'FINALIZING'
    const isCompletedList = list?.status === 'completed';
    const uiState = buildShoppingListUiState({ list, step });
    const displayListName = resolveDisplayListName(list);
    
    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const fetchFullDetails = async () => {
            const listId = Number(list?.id);
            if (!Number.isInteger(listId) || listId <= 0 || !isOpen) return;
            
            // Reset step when opening/changing list
            setStep((autoStartFinalizing || list?.status === 'awaiting_owner_confirmation') ? 'FINALIZING' : 'SHOPPING');
            
            // Check if we already have items (e.g. from a previous fetch or full object)
            // Projection lists have items: [] or partial items. 
            // In our new architecture, Projection has partial items for preview, but we want the full rich detail for the modal.
            
            setIsLoadingDetails(true);
            try {
                const token = localStorage.getItem('authToken');
                const res = await fetch(`${API_BASE_URL}/api/shopping/lists/${listId}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    signal: controller.signal
                });
                
                if (res.ok && isMounted) {
                    const fullList = await res.json();
                const enrichedItems = (fullList.items || []).map(item => ({
                    ...item,
                    checked: item.isChecked || false,
                    price: item.price || 0,
                    priceAtTime: item.priceAtTime || 0,
                    brand: item.brand || '',
                    alternativeProduct: item.alternativeProduct || '',
                    inventoryPlacement: item.inventoryPlacement || null
                }));
                    setItems(enrichedItems);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Failed to fetch list details:', error);
                }
            } finally {
                if (isMounted) setIsLoadingDetails(false);
            }
        };

        fetchFullDetails();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [list?.id, isOpen, autoStartFinalizing]);

    if (!list) return null;

    const toggleItemCheck = async (itemId) => {
        const item = items.find(i => i.id === itemId);
        if (!item) return;

        const nextChecked = !item.checked;
        setItems(prevItems => prevItems.map(i =>
            i.id === itemId ? { ...i, checked: nextChecked } : i
        ));

        try {
            const token = localStorage.getItem('authToken');
            await fetch(`${API_BASE_URL}/api/shopping/items/${itemId}/toggle`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ isChecked: nextChecked })
            });
        } catch (error) {
            console.error('Toggle error:', error);
            setItems(prevItems => prevItems.map(i =>
                i.id === itemId ? { ...i, checked: !nextChecked } : i
            ));
        }
    };

    const handleItemChange = (itemId, field, value) => {
        if (isCompletedList) return;
        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
        ));
    };

    const handleUnitChange = (itemId, unit) => {
        if (isCompletedList) return;
        const normalizeUnit = (value = '') => String(value || '').trim().toLocaleLowerCase('tr-TR');
        const convertAmount = (amount, fromUnit, toUnit) => {
            const numericAmount = Number(amount);
            if (!Number.isFinite(numericAmount)) return amount;

            const from = normalizeUnit(fromUnit);
            const to = normalizeUnit(toUnit);
            if (from === to) return amount;
            if (['g', 'gr', 'gram'].includes(from) && to === 'kg') return Number((numericAmount / 1000).toFixed(3));
            if (from === 'kg' && ['g', 'gr', 'gram'].includes(to)) return Number((numericAmount * 1000).toFixed(2));
            if (from === 'ml' && ['lt', 'l', 'litre'].includes(to)) return Number((numericAmount / 1000).toFixed(3));
            if (['lt', 'l', 'litre'].includes(from) && to === 'ml') return Number((numericAmount * 1000).toFixed(2));
            return amount;
        };

        setItems(prev => prev.map(item => {
            if (item.id !== itemId) return item;
            return {
                ...item,
                finalAmount: convertAmount(item.finalAmount ?? item.suggestedAmount, item.unit, unit),
                unit
            };
        }));
    };

    const handleDeleteItem = async (itemId) => {
        if (isCompletedList) return;
        const result = await Swal.fire({
            title: 'Ürün silinsin mi?',
            text: "Bu ürünü listeden kaldırmak istediğinize emin misiniz?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ff3b3b',
            cancelButtonColor: '#334155',
            confirmButtonText: 'Evet, sil',
            cancelButtonText: 'Vazgeç',
            background: '#0a0e17',
            color: '#fff'
        });

        if (!result.isConfirmed) return;

        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/shopping/lists/${list.id}/items/${itemId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                setItems(items.filter(item => item.id !== itemId));
                // Sync TopBar and Sidebar badges
                window.dispatchEvent(new Event('Alakart_SYNC'));
                window.dispatchEvent(new Event('shoppingListUpdated'));
            }
        } catch (error) { console.error('Delete error:', error); }
    };

    const handleCompletePurchase = async () => {
        if (isCompletedList) return;
        const boughtItems = items; // All remaining items are bought
        if (boughtItems.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Uyarı',
                text: 'Lütfen en az bir ürünü sepetine at şefim.',
                confirmButtonColor: '#ff3b3b',
                background: '#0a0e17',
                color: '#fff'
            });
            return;
        }

        setIsCompleting(true);
        try {
            const token = localStorage.getItem('authToken');
                const payload = {
                items: boughtItems.map(item => ({
                    id: item.id,
                    dictionaryId: item.dictionaryId || item.ingredientId || null,
                    ingredientId: item.ingredientId,
                    name: item.ingredientName,
                    finalAmount: parseFloat(item.finalAmount ?? item.suggestedAmount) || 0,
                    unit: item.unit,
                    price: parseFloat(item.price) || 0,
                    brand: item.brand || '',
                    alternativeProduct: item.alternativeProduct || '',
                    itemGroup: item.itemGroup,
                    inventoryPlacement: item.inventoryPlacement || null
                }))
            };

            const res = await fetch(`${API_BASE_URL}/api/shopping/lists/${list.id}/complete`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Harika!',
                    text: 'Seçili ürünler envanterine eklendi.',
                    confirmButtonColor: '#ff3b3b',
                    background: '#0a0e17',
                    color: '#fff'
                }).then(() => {
                    // Sync TopBar and Sidebar badges immediately
                    window.dispatchEvent(new Event('Alakart_SYNC'));
                    window.dispatchEvent(new Event('shoppingListUpdated'));
                    onComplete?.();
                    onClose();
                });
            }
        } catch (error) { console.error('Complete error:', error); } finally { setIsCompleting(false); }
    };

    const calculateTotal = () => {
        return items.reduce((sum, item) => {
            // Priority: Price entered by user > Price snapshot at the time of list creation
            const val = parseFloat(item.price) || parseFloat(item.priceAtTime) || 0;
            return sum + val;
        }, 0);
    };

    const getShareMessage = () => {
        return 'Bu haftaki ev ihtiyaçlarımız hazır.';
    };

    const handleShare = async () => {
        if (isSharing) return;

        try {
            setIsSharing(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/shopping/lists/${list.id}/share`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.shareLink) {
                throw new Error(payload?.error || 'Paylaşım linki oluşturulamadı.');
            }

            const shareUrl = buildPublicShareUrl(payload.shareLink);
            const shouldPromptPartner = payload?.shouldPromptPartner === true;
            const shareMessage = '';

            if (CURRENT_ORIGIN_IS_PRIVATE && !HAS_PUBLIC_SHARE_ORIGIN) {
                toast?.error?.('Bu paylaşım linki yerel ağ adresiyle üretildi. WhatsApp önizlemesi için public domain üzerinden açılmalı.');
            }

            if (navigator.share) {
                try {
                    await navigator.share({
                        title: list?.name || 'Bu Haftaki Ev İhtiyaçlarımız',
                        url: shareUrl
                    });

                    if (shouldPromptPartner) {
                        setShowPartnerInvite(true);
                    }
                    return;
                } catch (shareErr) {
                    if (shareErr?.name !== 'AbortError') {
                        console.error('Native share failed:', shareErr);
                    }
                }
            }

            const whatsappMessage = `${shareMessage}\n\nListeyi aç:\n${shareUrl}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`, '_blank', 'noopener,noreferrer');

            const result = await Swal.fire({
                icon: 'success',
                title: 'Paylaşım hazır',
                html: `<p class="mb-4">Paylaşım penceresi açıldı. İstersen linki ayrıca aşağıdan da kullanabilirsin.</p><input type="text" value="${shareUrl}" readonly class="w-full p-2 border rounded text-sm" />`,
                confirmButtonText: shouldPromptPartner ? 'Partner Ekle' : 'Tamam',
                showDenyButton: true,
                denyButtonText: 'Linki Kopyala',
                showCancelButton: shouldPromptPartner,
                cancelButtonText: 'Şimdi Değil',
                background: '#0a0e17',
                color: '#fff'
            });

            if (result.isDenied) {
                const copied = await copyToClipboard(shareUrl);
                if (copied) {
                    toast?.success?.('Link kopyalandı!');
                } else {
                    toast?.error?.('Link kopyalanamadı.');
                }
            }

            if (shouldPromptPartner && result.isConfirmed) {
                setShowPartnerInvite(true);
            }
        } catch (err) {
            console.error('Share fail:', err);
            Swal.fire({
                icon: 'error',
                title: 'Paylaşım başarısız',
                text: err.message || 'Paylaşım linki oluşturulamadı.',
                background: '#0a0e17',
                color: '#fff'
            });
        } finally {
            setIsSharing(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-xl flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 40 }}
                        className="bg-white dark:bg-[#0a0e17] rounded-[2.25rem] w-full max-w-md sm:max-w-2xl h-[calc(100vh-1.5rem)] max-h-[calc(100vh-1.5rem)] sm:h-auto sm:max-h-[90vh] flex flex-col shadow-[0_0_50px_rgba(0,0,0,0.5)] relative border border-slate-200 dark:border-white/5 overflow-hidden font-sans"
                    >
                        {/* Header Section */}
                        <div className="px-5 pt-5 pb-3 sm:px-8 sm:pt-7 sm:pb-4 flex justify-between items-start gap-4">
                            <div className="flex flex-col min-w-0">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-[#ff3b3b] text-[28px] sm:text-3xl">shopping_cart</span>
                                    <h2 className="text-[15px] leading-tight sm:text-[22px] font-black text-slate-900 dark:text-white tracking-tight uppercase break-words max-w-[220px] sm:max-w-none">
                                        {displayListName}
                                    </h2>
                                </div>
                                <div className="flex items-center gap-2 mt-1.5">
                                    <p className="text-[10px] font-bold text-slate-500 dark:text-gray-500 uppercase tracking-[0.2em]">
                                        {new Date(list.createdAt).toLocaleDateString('tr-TR')}
                                    </p>
                                    <span className="w-1 h-1 bg-white/10 rounded-full"></span>
                                    <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest">{items.length} Ürün</span>
                                </div>
                            </div>
                             <div className="flex items-center gap-2">
                                 {uiState.canDeleteList && (
                                     <button 
                                         onClick={() => onDeleteList?.(list.id)}
                                         className="size-10 flex items-center justify-center bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 hover:bg-red-500 hover:text-white transition-all shadow-lg shadow-red-500/10"
                                         title="Listeyi Sil"
                                     >
                                         <span className="material-symbols-outlined text-[20px]">delete</span>
                                     </button>
                                 )}
                                 <button onClick={onClose} className="size-10 flex items-center justify-center bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/5 rounded-xl text-slate-500 dark:text-gray-400 hover:text-white transition-colors">
                                     <span className="material-symbols-outlined text-[20px]">close</span>
                                 </button>
                             </div>
                        </div>

                        {/* Items List (Scrollable) */}
                        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4 space-y-3 sm:space-y-4 no-scrollbar">
                            {isLoadingDetails ? (
                                <div className="flex flex-col items-center justify-center py-20 gap-4">
                                    <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                                    <p className="text-[10px] font-black text-slate-500 dark:text-gray-500 uppercase tracking-[0.3em]">Liste Detayları Yükleniyor...</p>
                                </div>
                            ) : items.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 opacity-50">
                                    <span className="material-symbols-outlined text-5xl mb-2">inventory_2</span>
                                    <p className="text-xs font-bold text-slate-500 dark:text-gray-500">Bu liste boş şefim.</p>
                                </div>
                            ) : (
                                items
                                    .map((item) => (
                                        <ShoppingItemCard 
                                            key={item.id}
                                            item={item}
                                            onQuantityChange={uiState.canEditItems ? (id, val) => handleItemChange(id, 'finalAmount', val) : null}
                                            onUnitChange={uiState.canEditItems ? handleUnitChange : null}
                                            onBrandChange={uiState.canEditItems ? (id, val) => handleItemChange(id, 'brand', val) : null}
                                            onAlternativeProductChange={null}
                                            onPriceChange={uiState.canEditItems ? (id, val) => handleItemChange(id, 'price', val) : null}
                                            onPlacementChange={null}
                                            onDelete={uiState.canEditItems ? handleDeleteItem : null}
                                            onToggleCheck={null} // No more selection
                                            mode={uiState.cardMode}
                                        />
                                    ))
                            )}
                        </div>

                        {/* Footer (Total & Actions) */}
                        <div className="shrink-0 border-t border-slate-200 dark:border-white/5 bg-white dark:bg-[#0a0e17] px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pt-2">
                            <div className="flex items-center justify-between px-1 sm:px-2 mb-4 sm:mb-6 text-slate-500 dark:text-gray-400">
                                <span className="text-xs font-black uppercase tracking-widest opacity-50">Toplam Tutar</span>
                                <span className="text-2xl font-black text-[#ff3b3b] tracking-tighter italic">
                                    {formatTotalCurrency(calculateTotal())}
                                </span>
                            </div>

                            {step === 'FINALIZING' && (
                                <div className="mb-4 rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-[11px] font-bold leading-relaxed text-amber-100">
                                    SKT ve dolap yeri sistem tarafından ürün tipine göre atanacak. Burada sadece miktar ve ödenen fiyatı kontrol etmeniz yeterli.
                                </div>
                            )}

                            {isCompleting && (
                                <div className="mb-4 rounded-2xl border border-blue-500/20 bg-blue-500/10 px-4 py-3 text-[11px] font-bold text-blue-100 flex items-center gap-3">
                                    <div className="size-4 border-2 border-blue-200/30 border-t-blue-200 rounded-full animate-spin" />
                                    <span>Ürünler stoğa alınıyor, dolap yerleri ve SKT etiketleri işleniyor...</span>
                                </div>
                            )}

                            <div className="grid grid-cols-12 gap-3 items-center">
                                {/* Share/Back Action */}
                                 <div className="col-span-4">
                                     {uiState.leftAction === 'completed' ? (
                                         <div className="w-full h-14 bg-green-500/10 rounded-2xl flex items-center justify-center gap-2 text-green-400 font-black text-[10px] uppercase tracking-widest border border-green-500/20">
                                             <span className="material-symbols-outlined text-[18px]">check_circle</span>
                                             <span>Tamamlandı</span>
                                         </div>
                                     ) : uiState.leftAction === 'share' ? (
                                         <button 
                                             onClick={handleShare}
                                             disabled={isSharing}
                                             className="w-full h-14 bg-slate-100 dark:bg-white/5 rounded-2xl flex items-center justify-center gap-2 text-blue-400 font-black text-[10px] uppercase tracking-widest border border-slate-200 dark:border-white/5 hover:bg-white/10 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                                         >
                                             <span className="material-symbols-outlined text-[18px]">share</span>
                                             <span>{isSharing ? 'Hazırlanıyor...' : 'Paylaş'}</span>
                                         </button>
                                     ) : (
                                         <button 
                                             onClick={() => setStep('SHOPPING')}
                                             className="w-full h-14 bg-slate-100 dark:bg-white/5 rounded-2xl flex items-center justify-center gap-2 text-slate-500 dark:text-gray-400 font-black text-[10px] uppercase tracking-widest border border-slate-200 dark:border-white/5 hover:bg-white/10 transition-all active:scale-95"
                                         >
                                             <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                                             <span>Geri Dön</span>
                                         </button>
                                     )}
                                 </div>

                                {/* Complete Action */}
                                <div className="col-span-8">
                                    {uiState.rightAction === 'close' ? (
                                        <button
                                            onClick={onClose}
                                            className="w-full h-14 bg-slate-100 dark:bg-white/5 rounded-2xl flex items-center justify-center gap-3 text-white font-black text-[11px] uppercase tracking-[0.2em] border border-slate-200 dark:border-white/5 hover:bg-white/10 transition-all active:scale-[0.98]"
                                        >
                                            <span className="material-symbols-outlined text-[20px] font-black">close</span>
                                            <span>Kapat</span>
                                        </button>
                                    ) : uiState.rightAction === 'finalize' ? (
                                        <button
                                            onClick={() => setStep('FINALIZING')}
                                            className="w-full h-14 bg-blue-600 rounded-2xl flex items-center justify-center gap-3 text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-blue-600/20 hover:-translate-y-1 transition-all active:scale-[0.98]"
                                        >
                                            <span className="material-symbols-outlined text-[20px] font-black">shopping_basket</span>
                                            <span>Alışverişi Bitir</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleCompletePurchase}
                                            disabled={isCompleting}
                                            className="w-full h-14 bg-[#ff3b3b] rounded-2xl flex items-center justify-center gap-3 text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-[0_10px_30px_rgba(255,59,59,0.3)] hover:-translate-y-1 transition-all active:scale-[0.98] disabled:opacity-50"
                                        >
                                            {isCompleting ? (
                                                <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <span className="material-symbols-outlined text-[20px] font-black">verified</span>
                                            )}
                                            <span>Envantere Aktar</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            <PartnerInviteModal
                isOpen={showPartnerInvite}
                onClose={() => setShowPartnerInvite(false)}
                onInviteSent={() => setShowPartnerInvite(false)}
            />
        </AnimatePresence>
    );
};

export default ShoppingListDetailModal;
